/**
 * Parts broken off a vehicle: a bumper, a mirror, a lamp, a plate, falling,
 * bouncing and tumbling to rest on the ground.
 *
 * Each piece is a rigid box (its own bounds) with a velocity and a spin.
 * Gravity pulls it down; where a corner goes under the ground, an impulse at
 * that corner stops it going further, bounces it a little (a quarter of the
 * speed it came in at) and drags it along by friction, which is also what
 * sets it spinning end over end. Once it has lain still a moment it sleeps
 * and costs nothing. Only the ground is felt, not walls or other pieces: a
 * piece flies off a car near the ground, and what it might hit on its way
 * is a small matter beside it landing right.
 */

import type { Vec3 } from './damage.js';

/** x, y, z, w. */
export type Quat = [number, number, number, number];

export interface Piece {
  /** Its middle, scene metres. */
  p: Vec3;
  q: Quat;
  /** Velocity, m/s, and spin, rad/s, both in the scene's frame. */
  v: Vec3;
  w: Vec3;
  /** Half its size along its own axes, metres. */
  half: Vec3;
  /** Asleep on the ground: no longer moved. */
  resting: boolean;
  /** Seconds it has been all but still on the ground. */
  still: number;
}

/**
 * The ground's height at a point, or undefined where it is not known yet.
 * `y` is how high the point is: a bridge's deck counts under a piece on it,
 * not under one falling past it to the road below.
 */
export type Ground = (x: number, z: number, y: number) => number | undefined;

const GRAVITY = 9.81;
/** How much of its speed into the ground a piece keeps, bouncing back. */
const BOUNCE = 0.25;
/** Coming down slower than this, m/s, a corner settles rather than bouncing. */
const BOUNCES = 0.6;
/** Sliding friction against the ground. */
const FRICTION = 0.6;
/** Steps no longer than this, seconds, so a fast piece does not pass through the ground between them. */
const STEP = 1 / 120;

/** Rotate v by unit quaternion q, into `out`. */
export function rotate(q: Quat, v: Vec3, out: Vec3): Vec3 {
  const [x, y, z, w] = q;
  // t = 2 (q.xyz × v); v' = v + w t + q.xyz × t
  const tx = 2 * (y * v[2] - z * v[1]), ty = 2 * (z * v[0] - x * v[2]), tz = 2 * (x * v[1] - y * v[0]);
  out[0] = v[0] + w * tx + (y * tz - z * ty);
  out[1] = v[1] + w * ty + (z * tx - x * tz);
  out[2] = v[2] + w * tz + (x * ty - y * tx);
  return out;
}

const cross = (a: Vec3, b: Vec3, out: Vec3): Vec3 => {
  const x = a[1] * b[2] - a[2] * b[1], y = a[2] * b[0] - a[0] * b[2], z = a[0] * b[1] - a[1] * b[0];
  out[0] = x; out[1] = y; out[2] = z;
  return out;
};

const corner: Vec3 = [0, 0, 0];
const arm: Vec3 = [0, 0, 0];
const tmp: Vec3 = [0, 0, 0];
const tmp2: Vec3 = [0, 0, 0];
const local: Vec3 = [0, 0, 0];
const inverse: Quat = [0, 0, 0, 1];

/**
 * The world inverse inertia applied to `torque`-like vector `a` (per unit
 * mass), into `out`: the box's own inertia, turned with it.
 */
function inverseInertia(piece: Piece, a: Vec3, out: Vec3): Vec3 {
  const [hx, hy, hz] = piece.half;
  // A solid box of unit mass: I = (b² + c²) / 3 for half-sizes.
  const ix = (hy * hy + hz * hz) / 3, iy = (hx * hx + hz * hz) / 3, iz = (hx * hx + hy * hy) / 3;
  inverse[0] = -piece.q[0]; inverse[1] = -piece.q[1]; inverse[2] = -piece.q[2]; inverse[3] = piece.q[3];
  rotate(inverse, a, local);
  local[0] /= Math.max(ix, 1e-5);
  local[1] /= Math.max(iy, 1e-5);
  local[2] /= Math.max(iz, 1e-5);
  return rotate(piece.q, local, out);
}

/** Push the piece by impulse `j` (per unit mass) at arm `r` from its middle. */
function apply(piece: Piece, r: Vec3, j: Vec3): void {
  piece.v[0] += j[0]; piece.v[1] += j[1]; piece.v[2] += j[2];
  inverseInertia(piece, cross(r, j, tmp), tmp2);
  piece.w[0] += tmp2[0]; piece.w[1] += tmp2[1]; piece.w[2] += tmp2[2];
}

/** How hard it is to change the speed at arm `r` along unit `n`: 1 / effective mass, per unit mass. */
function softness(piece: Piece, r: Vec3, n: Vec3): number {
  inverseInertia(piece, cross(r, n, tmp), tmp2);
  cross(tmp2, r, tmp);
  return 1 + tmp[0] * n[0] + tmp[1] * n[1] + tmp[2] * n[2];
}

const up: Vec3 = [0, 1, 0];
const heights = new Float64Array(8);
const along: Vec3 = [0, 0, 0];
const j: Vec3 = [0, 0, 0];

function substep(piece: Piece, dt: number, ground: Ground): void {
  piece.v[1] -= GRAVITY * dt;
  for (let k = 0; k < 3; k++) piece.p[k] += piece.v[k] * dt;
  // Turn by the spin: q += ½ (w, 0) q dt.
  const [qx, qy, qz, qw] = piece.q, [wx, wy, wz] = piece.w;
  piece.q[0] += 0.5 * dt * (wx * qw + wy * qz - wz * qy);
  piece.q[1] += 0.5 * dt * (wy * qw + wz * qx - wx * qz);
  piece.q[2] += 0.5 * dt * (wz * qw + wx * qy - wy * qx);
  piece.q[3] += 0.5 * dt * (-wx * qx - wy * qy - wz * qz);
  const n = Math.hypot(...piece.q) || 1;
  for (let k = 0; k < 4; k++) piece.q[k] /= n;

  let deepest = 0, touching = false;
  // Twice over the corners: a piece lying on several at once settles
  // rather than rocking from one to the next.
  for (let pass = 0; pass < 16; pass++) {
    const c = pass % 8;
    corner[0] = (c & 1 ? 1 : -1) * piece.half[0];
    corner[1] = (c & 2 ? 1 : -1) * piece.half[1];
    corner[2] = (c & 4 ? 1 : -1) * piece.half[2];
    rotate(piece.q, corner, arm);
    const y = piece.p[1] + arm[1];
    // The ground under each corner, looked up once a step: only the velocities change between passes.
    if (pass < 8) heights[c] = ground(piece.p[0] + arm[0], piece.p[2] + arm[2], y) ?? NaN;
    const h = heights[c];
    if (Number.isNaN(h) || y > h) continue;
    touching = true;
    deepest = Math.max(deepest, h - y);
    // Bounce only off the first touch; the second pass only stops it going in.
    const bounce = pass < 8;
    // How fast that corner moves: v + w × r.
    cross(piece.w, arm, tmp);
    const vx = piece.v[0] + tmp[0], vy = piece.v[1] + tmp[1], vz = piece.v[2] + tmp[2];
    if (vy >= 0) continue;
    // Stop it going in, and bounce a little if it came down hard: a corner
    // resting on the ground does not bounce, or it rocks for ever.
    const jn = (-(1 + (bounce && vy < -BOUNCES ? BOUNCE : 0)) * vy) / softness(piece, arm, up);
    j[0] = 0; j[1] = jn; j[2] = 0;
    apply(piece, arm, j);
    // Friction against the way it slides, no more than stops the slide.
    const slide = Math.hypot(vx, vz);
    if (slide < 1e-6) continue;
    along[0] = vx / slide; along[1] = 0; along[2] = vz / slide;
    const jt = Math.min(FRICTION * jn, slide / softness(piece, arm, along));
    j[0] = -along[0] * jt; j[1] = 0; j[2] = -along[2] * jt;
    apply(piece, arm, j);
  }
  // Out of the ground, without adding speed.
  piece.p[1] += deepest;
  if (!touching) {
    piece.still = 0;
    return;
  }
  // Scraping and rocking on the ground wear it down to a stop.
  const spin = Math.exp(-dt * 4), slide = Math.exp(-dt * 1.5);
  for (let k = 0; k < 3; k++) piece.w[k] *= spin;
  piece.v[0] *= slide;
  piece.v[2] *= slide;
  if (Math.hypot(...piece.v) < 0.15 && Math.hypot(...piece.w) < 0.5) piece.still += dt;
  else piece.still = 0;
  if (piece.still > 0.4) {
    piece.resting = true;
    piece.v[0] = piece.v[1] = piece.v[2] = 0;
    piece.w[0] = piece.w[1] = piece.w[2] = 0;
  }
}

/** Move a piece on by `dt` seconds over `ground`. A resting piece stays put. */
export function stepPiece(piece: Piece, dt: number, ground: Ground): void {
  if (piece.resting || dt <= 0) return;
  const steps = Math.min(30, Math.ceil(dt / STEP));
  for (let i = 0; i < steps; i++) substep(piece, dt / steps, ground);
}

/** The lowest of a piece's corners, scene metres. */
export function lowestCorner(piece: Piece): number {
  let low = Infinity;
  for (let c = 0; c < 8; c++) {
    corner[0] = (c & 1 ? 1 : -1) * piece.half[0];
    corner[1] = (c & 2 ? 1 : -1) * piece.half[1];
    corner[2] = (c & 4 ? 1 : -1) * piece.half[2];
    rotate(piece.q, corner, arm);
    low = Math.min(low, piece.p[1] + arm[1]);
  }
  return low;
}
